  // client/src/components/admin/RoleBadge.jsx
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  Person as PersonIcon,
  Visibility as CuratorIcon,
  AdminPanelSettings as AdminIcon,
  VerifiedUser as SuperAdminIcon,
} from '@mui/icons-material';

// ============================================================================
// Конфигурация ролей
// ============================================================================

const ROLE_CONFIG = {
  user: {
    label: 'Игрок',
    short: 'user',
    color: 'default',
    variant: 'outlined',
    icon: PersonIcon,
    tooltip: 'Обычный игрок: создание персонажей, игра, выбор мира',
  },
  curator: {
    label: 'Куратор',
    short: 'curator',
    color: 'info',
    variant: 'filled',
    icon: CuratorIcon,
    tooltip: 'Наблюдает за субъектами других игроков, может эмансипировать',
  },
  admin: {
    label: 'Администратор',
    short: 'admin',
    color: 'warning',
    variant: 'filled',
    icon: AdminIcon,
    tooltip: 'Управляет онтологиями, правилами, сервисами, пользователями',
  },
  superadmin: {
    label: 'Суперадминистратор',
    short: 'superadmin',
    color: 'error',
    variant: 'filled',
    icon: SuperAdminIcon,
    tooltip: 'Полный доступ, destructive-операции',
  },
};

const FALLBACK_CONFIG = {
  label: 'Неизвестно',
  short: '?',
  color: 'default',
  variant: 'outlined',
  icon: PersonIcon,
  tooltip: 'Роль не определена',
};

// ============================================================================
// Компонент
// ============================================================================

/**
 * Цветной бейдж роли.
 *
 * @param {object} props
 * @param {'user'|'curator'|'admin'|'superadmin'} props.role
 * @param {'small'|'medium'} [props.size='small']
 * @param {boolean} [props.showIcon=true]
 * @param {boolean} [props.showTooltip=true]
 * @param {'label'|'short'} [props.textMode='label'] — что показывать: русское название или технический код
 */
export default function RoleBadge({
  role,
  size = 'small',
  showIcon = true,
  showTooltip = true,
  textMode = 'label',
}) {
  const config = ROLE_CONFIG[role] || FALLBACK_CONFIG;
  const IconComponent = config.icon;
  const text = textMode === 'short' ? config.short : config.label;

  const chip = (
    <Chip
      label={text}
      color={config.color}
      variant={config.variant}
      size={size}
      icon={showIcon ? <IconComponent /> : undefined}
      sx={{
        fontWeight: role === 'superadmin' ? 'bold' : 'normal',
        '& .MuiChip-icon': { fontSize: size === 'small' ? 14 : 18 },
      }}
    />
  );

  if (!showTooltip) return chip;

  return (
    <Tooltip title={config.tooltip} arrow>
      {chip}
    </Tooltip>
  );
}

// ============================================================================
// Экспорт конфигурации (для использования в других компонентах)
// ============================================================================

export { ROLE_CONFIG };
export const ROLE_OPTIONS = Object.entries(ROLE_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
  tooltip: cfg.tooltip,
}));